import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import {
  Granularity,
  TimeSeriesPoint,
  ModuleTimeSeriesPoint,
  ModuleEventBreakdownPoint,
  TopUserPoint,
  AnalyticsModule,
  PayloadBreakdownPoint,
} from '../types';

export function useEventNames() {
  const getToken = useAccessToken();
  const query = useQuery<string[]>({
    queryKey: ['usageAnalytics', 'eventNames'],
    queryFn: () =>
      fetchApi<{ data: string[] }>('/api/usage-analytics/events/names', getToken).then(
        (r) => r.data,
      ),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
  return {
    eventNames: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useEventTimeSeries(
  eventName: string | null,
  granularity: Granularity,
  days: number,
  cluster?: string | null,
) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ granularity, days: String(days) });
  if (eventName) params.set('event_name', eventName);
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<TimeSeriesPoint[]>({
    queryKey: ['usageAnalytics', 'eventTimeSeries', eventName, granularity, days, cluster ?? ''],
    queryFn: () =>
      fetchApi<{ data: TimeSeriesPoint[] }>(
        `/api/usage-analytics/events/time-series?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    enabled: !!eventName,
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useModuleTimeSeries(granularity: Granularity, days: number, cluster?: string | null) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ granularity, days: String(days) });
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<ModuleTimeSeriesPoint[]>({
    queryKey: ['usageAnalytics', 'moduleTimeSeries', granularity, days, cluster ?? ''],
    queryFn: () =>
      fetchApi<{ data: ModuleTimeSeriesPoint[] }>(
        `/api/usage-analytics/modules/time-series?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useModuleEventBreakdown(granularity: Granularity, days: number, cluster?: string | null) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ granularity, days: String(days) });
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<ModuleEventBreakdownPoint[]>({
    queryKey: ['usageAnalytics', 'moduleEventBreakdown', granularity, days, cluster ?? ''],
    queryFn: () =>
      fetchApi<{ data: ModuleEventBreakdownPoint[] }>(
        `/api/usage-analytics/modules/event-breakdown?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useClusters() {
  const getToken = useAccessToken();
  const query = useQuery<string[]>({
    queryKey: ['usageAnalytics', 'clusters'],
    queryFn: () =>
      fetchApi<{ data: string[] }>('/api/usage-analytics/clusters', getToken).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
  return {
    clusters: query.data ?? [],
    isLoading: query.isLoading,
  };
}

export function useTopUsers(
  days: number,
  limit: number,
  eventName?: string | null,
  cluster?: string | null,
) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (eventName) params.set('event_name', eventName);
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<TopUserPoint[]>({
    queryKey: ['usageAnalytics', 'topUsers', days, limit, eventName ?? '', cluster ?? ''],
    queryFn: () =>
      fetchApi<{ data: TopUserPoint[] }>(
        `/api/usage-analytics/users/top?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
  };
}

interface RawEvent {
  timestamp: string;
  user_email: string;
  user_name: string;
  event_name: string;
  source_cluster: string;
}

export function useRawEvents(  eventName: string | null,
  days: number = 7,
  cluster?: string | null,
  limit: number = 10000,
) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (eventName) params.set('event_name', eventName);
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<RawEvent[]>({
    queryKey: ['usageAnalytics', 'rawEvents', eventName, days, cluster ?? '', limit],
    queryFn: () =>
      fetchApi<{ data: RawEvent[] }>(
        `/api/usage-analytics/events/raw?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    enabled: !!eventName,
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function usePageViewStats(days: number = 7, cluster?: string | null) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ days: String(days) });
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<{ page: string; count: number }[]>({
    queryKey: ['usageAnalytics', 'pageViews', days, cluster ?? ''],
    queryFn: () =>
      fetchApi<{ data: { page: string; count: number }[] }>(
        `/api/usage-analytics/pageviews?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useEventPayloadBreakdown(
  eventName: string | null,
  payloadField: string | null,
  days: number,
  cluster?: string | null,
) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ days: String(days) });
  if (eventName) params.set('event_name', eventName);
  if (payloadField) params.set('field', payloadField);
  if (cluster) params.set('cluster', cluster);

  const query = useQuery<PayloadBreakdownPoint[]>({
    queryKey: ['usageAnalytics', 'payloadBreakdown', eventName, payloadField, days, cluster ?? ''],
    queryFn: () =>
      fetchApi<{ data: PayloadBreakdownPoint[] }>(
        `/api/usage-analytics/events/payload-breakdown?${params.toString()}`,
        getToken,
      ).then((r) => r.data),
    enabled: !!eventName && !!payloadField,
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
}

export function useModuleConfig() {
  const getToken = useAccessToken();
  const query = useQuery<AnalyticsModule[]>({
    queryKey: ['usageAnalytics', 'moduleConfig'],
    queryFn: () =>
      fetchApi<{ data: AnalyticsModule[] }>('/api/usage-analytics/module-config', getToken).then(
        (r) => r.data,
      ),
    staleTime: 60 * 1000,
    retry: 2,
  });
  return {
    modules: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
